"use client";

import ClickOutside from "@faire/web--source/ui/ClickOutside";
import * as React from "react";

import { ClearButton } from "./__internal__/ClearButton";
import { DropdownContent } from "./__internal__/DropdownContent";
import { DropdownInput } from "./__internal__/DropdownInput";
import { MobileDropdown } from "./__internal__/MobileDropdown";
import { useDropdownState } from "./__internal__/useDropdownState";
import {
  getLabel,
  getModularIndex,
  getValue,
  isCustomOption,
} from "./__internal__/utils";
import { Wrapper } from "./styles";
import { IProps } from "./types";

/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/slate/component/dropdown)
 */
export const Dropdown = <T extends string = string>(props: IProps<T>) => {
  const {
    options,
    filter,
    onSelect,
    value,
    containingElement,
    disableInput,
    className,
    placeholder,
    label,
    error,
    helper,
    createNewOption,
    disabled,
    autoComplete = "off",
    id,
    inputRef,
    borderless,
    customOption,
    "aria-label": ariaLabel,
    "data-test-id": dataTestId,
    "data-hj-allow": dataHjAllow,
    useTabletAndAboveDropdownStyle,
    onDropdownOpen,
    onDropdownClose,
    customOptionMaxLength,
  } = props;

  const {
    dropdownId,
    targetRef,
    refs,
    open,
    setOpen,
    searchValue,
    setSearchValue,
    highlightIndex,
    setHighlightIndex,
    isCustomizingOption,
    setIsCustomizingOption,
    filteredOptions,
    ariaProps,
    showErrorState,
    showErrorMessage,
    errorMessage,
    showMobileDropdown,
  } = useDropdownState({
    id,
    inputRef,
    filter,
    options,
    customOption,
    createNewOption,
    ariaLabel,
    label,
    error,
    useTabletAndAboveDropdownStyle,
  });

  const handleInputOnChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    if (!isCustomizingOption) {
      handleDropdownOpen();

      const search = event.target.value;
      setSearchValue(search);

      if (search === "") {
        setHighlightIndex(0);
        scrollIntoView(0);
        return;
      }

      const optionIndex = filteredOptions.findIndex((e) =>
        getLabel(e).toLowerCase().startsWith(search.toLowerCase())
      );
      if (optionIndex !== -1) {
        setHighlightIndex(optionIndex);
        scrollIntoView(optionIndex);
      }
    } else {
      setSearchValue(event.target.value);
    }
  };

  const enterCustomValue = () => {
    if (customOption && searchValue) {
      customOption.onValueChange(searchValue);
      setIsCustomizingOption(false);
      setSearchValue(undefined);
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter") {
      if (isCustomizingOption) {
        enterCustomValue();
      } else if (props.createNewOption && filteredOptions.length === 0) {
        setSearchValue(undefined);
        if (props.createNewOption.closeDropdown) {
          setOpen(false);
        }
        props.createNewOption?.onClick?.(searchValue);
      }
    }
  };

  const handleClickOutside = () => {
    if (isCustomizingOption) {
      enterCustomValue();
    } else {
      handleDropdownClose();
    }
  };

  const handleSelectDropDown = (optionValue: T) => {
    let option = undefined;
    if (customOption && getValue(customOption) === optionValue) {
      option = customOption;
    } else {
      option = options.find((e) => getValue(e) === optionValue);
    }
    onSelect(option!);
    setOpen(false);
    setSearchValue(undefined);
  };

  const handleDropdownOpen = () => {
    onDropdownOpen?.();
    setOpen(true);
    if (!value) {
      return;
    }

    const selectedIndex =
      customOption && customOption.value === value
        ? options.length
        : options.findIndex((o) => o.value === value);

    if (selectedIndex !== -1) {
      setHighlightIndex(selectedIndex);
    }
  };

  const handleDropdownClose = () => {
    onDropdownClose?.();
    setSearchValue(undefined);
    setOpen(false);
  };

  const handleSelectDropDownItem = (
    event: React.MouseEvent<HTMLElement>,
    index: number
  ) => {
    event.preventDefault();
    select(index);
  };

  const handleEditCustomOption = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setIsCustomizingOption(true);

    handleDropdownClose();
    if (targetRef.current) {
      targetRef.current.focus();
    }

    setSearchValue(customOption?.hasEdited ? customOption.value : "");
  };

  const select = (selectIndex: number) => {
    const selectedOption = filteredOptions[selectIndex];
    const value = selectedOption ? getValue(selectedOption) : undefined;

    if (value !== undefined) {
      handleSelectDropDown(value);
    }
  };

  const scrollIntoView = (index: number) => {
    const ref = refs.current[index];
    if (!ref) {
      return;
    }
    ref.scrollIntoView?.({
      block: "nearest",
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (["Tab", "Escape"].includes(event.key)) {
      handleDropdownClose();
      return;
    }

    const isRelevantKey = ["ArrowDown", "ArrowUp", "Enter"].includes(event.key);
    if (!isRelevantKey) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (open) {
      let nextIndex = highlightIndex;
      switch (event.key) {
        case "ArrowDown":
        case "ArrowUp":
          nextIndex = getNextIndex(event.key);
          break;
        case "Enter":
          select(highlightIndex);
          break;
        default:
          break;
      }

      scrollIntoView(nextIndex);
      setHighlightIndex(nextIndex);
    } else {
      handleDropdownOpen();
    }
  };

  /**
   * get the next non-disabled index
   */
  const getNextIndex = (eventKey: "ArrowDown" | "ArrowUp"): number => {
    const optionsCount = filteredOptions.length;
    let nextIndex = highlightIndex;

    for (let i = 0; i < optionsCount; ++i) {
      nextIndex = nextIndex + (eventKey === "ArrowDown" ? 1 : -1);
      nextIndex = getModularIndex(nextIndex, optionsCount);

      const nextOption = filteredOptions[nextIndex];
      if (nextOption && !nextOption.disabled) {
        return nextIndex;
      }
    }
    // we've iterated over all possibilities, let's keep the same option
    return highlightIndex;
  };

  const onHighlight = (index: number) => () => {
    setHighlightIndex(index);
  };

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (targetRef.current) {
      targetRef.current.setSelectionRange(0, targetRef.current.value.length);
    }
    if (open) {
      handleDropdownClose();
    } else {
      handleDropdownOpen();
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const currentSelectedLabel = filteredOptions.find(
    (o) => o.value === value
  )?.label;

  const displayValue = disableInput
    ? currentSelectedLabel ?? ""
    : searchValue ?? currentSelectedLabel ?? "";

  if (showMobileDropdown) {
    return (
      <MobileDropdown
        borderless={borderless}
        className={className}
        dataTestId={dataTestId}
        disabled={disabled}
        errorMessage={errorMessage}
        options={options}
        helper={helper}
        id={dropdownId}
        label={label}
        onOpen={onDropdownOpen}
        onSelect={handleSelectDropDown}
        placeholder={placeholder}
        showErrorMessage={showErrorMessage}
        showErrorState={showErrorState}
        value={value}
      />
    );
  }

  return (
    <Wrapper
      data-test-id={`dropdown-${label || "default"}`}
      className={className}
      $disabled={disabled}
      $disableInput={disableInput}
      {...ariaProps}
    >
      <ClickOutside
        onClickOutside={handleClickOutside}
        onKeyDown={handleKeyDown}
      >
        <DropdownInput
          id={dropdownId}
          dataTestId={dataTestId}
          disabled={disabled}
          label={label}
          helper={helper}
          error={error}
          displayValue={displayValue}
          placeholder={placeholder}
          disableSearch={disableInput}
          onChange={handleInputOnChange}
          onClick={handleInputClick}
          onKeyDown={handleInputKeyDown}
          targetRef={targetRef}
          borderless={borderless}
          autoComplete={autoComplete}
          ariaLabel={ariaLabel}
          value={value}
          dataHjAllow={dataHjAllow}
          onClear={props.onClear}
          maxLength={customOptionMaxLength}
        />
        {open ? (
          <DropdownContent
            createNewOption={createNewOption}
            searchValue={searchValue}
            setOpen={setOpen}
            setSearchValue={setSearchValue}
            showErrorMessage={showErrorMessage}
            helper={helper}
            open={open}
            filteredOptions={filteredOptions}
            isCustomOption={isCustomOption}
            handleSelectDropDownItem={handleSelectDropDownItem}
            handleEditCustomOption={handleEditCustomOption}
            highlightIndex={highlightIndex}
            refs={refs}
            onHighlight={onHighlight}
            targetRef={targetRef}
            containingElement={containingElement}
            options={options}
          />
        ) : null}
      </ClickOutside>
      <ClearButton value={value} onClear={props.onClear} helper={helper} />
    </Wrapper>
  );
};

Dropdown.displayName = "Dropdown";
